// Team and review types for multi-reviewer collaboration

export type ReviewMode = 'ai_only' | 'ai_human' | 'dual_human' | 'full';
export type DecisionSource = 'ai' | 'human' | 'ai_confirmed';

export interface ReviewSettings {
  include_threshold: number;
  exclude_threshold: number;
  exclusion_reasons: string[];
  require_exclusion_reason: boolean;
  screening_order: 'sequential' | 'priority';
}

export const DEFAULT_REVIEW_SETTINGS: ReviewSettings = {
  include_threshold: 70,
  exclude_threshold: 40,
  exclusion_reasons: [
    'Wrong population',
    'Wrong study design',
    'Not primary research',
    'Full text not available',
    'Not in English',
    'Duplicate',
    'Wrong intervention',
    'Wrong outcome',
    'Conference abstract only',
  ],
  require_exclusion_reason: true,
  screening_order: 'sequential',
};

export interface Team {
  id: string;
  name: string;
  owner_id: string;
  created_at: string;
}

export type TeamRole = 'owner' | 'admin' | 'reviewer' | 'viewer';

export interface TeamMember {
  id: string;
  team_id: string;
  user_id: string;
  role: TeamRole;
  invited_at: string;
  accepted_at: string | null;
  // Joined fields
  profile?: {
    email: string;
    full_name: string | null;
    avatar_url: string | null;
  };
}

export interface TeamWithMembers extends Team {
  members: TeamMember[];
  member_count: number;
}

export type ReviewStatus = 'screening' | 'full_text' | 'extraction' | 'completed';

export interface Review {
  id: string;
  team_id: string;
  name: string;
  description: string | null;
  status: ReviewStatus;
  blinded: boolean;
  review_mode: ReviewMode;
  settings: ReviewSettings;
  created_at: string;
  updated_at: string;
}

export type ReviewDecisionType = 'include' | 'exclude' | 'maybe';
export type ReviewPhase = 'screening' | 'full_text';

export interface ReviewDecision {
  id: string;
  review_id: string;
  source_id: string;
  reviewer_id: string;
  decision: ReviewDecisionType;
  reason: string | null;
  phase: ReviewPhase;
  decision_source: DecisionSource;
  exclusion_reason_category: string | null;
  created_at: string;
  // Joined fields
  reviewer?: {
    full_name: string | null;
    email: string;
  };
}

export interface ReviewSource {
  id: string;
  review_id: string;
  source_id: string;
  current_phase: 'screening' | 'full_text' | 'completed';
  screening_priority: number;
  added_at: string;
  // AI screening fields (populated after AI scoring)
  ai_score?: number | null;      // 0-100 from screening
  ai_decision?: ReviewDecisionType | null; // include/maybe/exclude
  // Joined fields
  source?: {
    id: string;
    file_name: string;
    title: string | null;
    authors: string[] | null;
    abstract: string | null;
    publication_year: number | null;
    journal: string | null;
    doi: string | null;
  };
}

export interface ReviewConflict {
  id: string;
  review_id: string;
  source_id: string;
  phase: string;
  resolved: boolean;
  resolved_by: string | null;
  final_decision: 'include' | 'exclude' | null;
  resolved_at: string | null;
  // Joined fields
  decisions?: ReviewDecision[];
  source?: {
    file_name: string;
    title: string | null;
  };
}

export interface ReviewStats {
  total_sources: number;
  screened: number;
  included: number;
  excluded: number;
  maybe: number;
  conflicts: number;
  resolved_conflicts: number;
}

export interface ReviewerStats {
  reviewer_id: string;
  reviewer_name: string;
  decisions_count: number;
  include_count: number;
  exclude_count: number;
  maybe_count: number;
  agreement_rate: number;
}

export interface InviteMemberPayload {
  team_id: string;
  email: string;
  role: TeamRole;
}

export interface CreateReviewPayload {
  team_id: string;
  name: string;
  description?: string;
  blinded?: boolean;
  review_mode?: ReviewMode;
  settings?: Partial<ReviewSettings>;
}

export interface SubmitDecisionPayload {
  review_id: string;
  source_id: string;
  decision: ReviewDecisionType;
  reason?: string;
  phase: ReviewPhase;
  decision_source?: DecisionSource;
  exclusion_reason_category?: string;
}

export interface ResolveConflictPayload {
  conflict_id: string;
  final_decision: 'include' | 'exclude';
}

// =========================================
// Screening Recommendation types (migration 021)
// =========================================

export interface ScreeningRecommendationRecord {
  id: string;
  review_id: string;
  source_id: string;
  phase: ReviewPhase;
  policy_version: string;
  adjudicator_version: string | null;
  final_recommendation: ReviewDecisionType;
  recommendation_confidence: number | null;
  risk_level: 'low' | 'medium' | 'high';
  requires_human_review: boolean;
  signals: unknown[];
  criteria_trace: unknown[];
  llm_trace: unknown | null;
  created_at: string;
  updated_at: string;
}

// =========================================
// Exclusion Audit types (migration 022)
// =========================================

export type ExclusionAuditStatus = 'pending' | 'assigned' | 'resolved';
export type ExclusionAuditResolution = 'confirm_exclude' | 'override_include' | 'override_maybe';

export interface ExclusionAuditRecord {
  id: string;
  review_id: string;
  source_id: string;
  phase: ReviewPhase;
  recommendation_id: string | null;
  audit_status: ExclusionAuditStatus;
  audit_reason: string;
  audit_risk_score: number | null;
  assigned_to: string | null;
  resolved_by: string | null;
  resolution: ExclusionAuditResolution | null;
  resolution_note: string | null;
  created_at: string;
  resolved_at: string | null;
  source?: {
    file_name: string;
    title: string | null;
  };
}

export interface ResolveExclusionAuditPayload {
  audit_id: string;
  resolution: ExclusionAuditResolution;
  resolution_note?: string;
}

// =========================================
// Priority Prediction types (migration 023)
// =========================================

export interface PriorityPredictionRecord {
  id: string;
  review_id: string;
  source_id: string;
  phase: ReviewPhase;
  model_version: string;
  priority_score: number;
  calibrated_probability: number | null;
  ranking_features: Record<string, unknown>;
  top_reasons: string[];
  label_snapshot: Record<string, unknown>;
  created_at: string;
}

// =========================================
// Screening Feedback types (migration 024)
// =========================================

export type ScreeningFeedbackType = 'agree' | 'override';

export interface ScreeningFeedbackRecord {
  id: string;
  user_id: string;
  review_id: string;
  source_id: string;
  recommendation_id: string;
  ai_recommendation: ReviewDecisionType;
  human_decision: ReviewDecisionType;
  feedback_type: ScreeningFeedbackType;
  feedback_note: string | null;
  created_at: string;
  updated_at: string;
}
